import * as React from "react"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

export interface LoadingOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Loading message to display */
  message?: string
  /** Whether to show a full-screen overlay or just inline */
  fullScreen?: boolean
  /** Whether the overlay should have a semi-transparent background */
  overlay?: boolean
  /** Size of the spinner */
  size?: "sm" | "md" | "lg"
}

const LoadingOverlay = React.forwardRef<HTMLDivElement, LoadingOverlayProps>(
  ({
    message,
    fullScreen = false,
    overlay = true,
    size = "md",
    className,
    ...props
  }, ref) => {
    const sizeClasses = {
      sm: {
        spinner: "size-5",
        text: "text-xs",
        gap: "gap-2",
      },
      md: {
        spinner: "size-8",
        text: "text-sm",
        gap: "gap-3",
      },
      lg: {
        spinner: "size-12",
        text: "text-base",
        gap: "gap-4",
      },
    }

    const styles = sizeClasses[size]

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-center",
          styles.gap,
          fullScreen && "fixed inset-0 z-50",
          !fullScreen && "absolute inset-0",
          overlay && "bg-background/80 backdrop-blur-xs",
          className
        )}
        {...props}
      >
        <Loader2
          className={cn(
            "animate-spin text-primary",
            styles.spinner
          )}
        />

        {message && (
          <p
            className={cn(
              "text-muted-foreground font-medium",
              styles.text
            )}
          >
            {message}
          </p>
        )}
      </div>
    )
  }
)
LoadingOverlay.displayName = "LoadingOverlay"

export { LoadingOverlay }
